<template>
  <div class="event-map-container">
    <!-- Map Container -->
    <div
      ref="mapContainer"
      class="map-canvas"
      role="application"
      aria-label="Events map - Click markers to view event details"
    ></div>

    <!-- Info Panel -->
    <div class="map-info-panel">
      <div class="info-content">
        <i class="bi bi-info-circle me-2"></i>
        <span>Click on event markers to view details and register</span>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="map-loading" role="status" aria-live="polite">
      <div class="spinner-border text-primary"></div>
      <p>Loading map...</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EventMap',
  props: {
    events: {
      type: Array,
      required: true
    },
    center: {
      type: Object,
      default: () => ({ lat: -37.8136, lng: 144.9631 }) // Melbourne
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      geocoder: null,
      loading: true,
      infoWindow: null
    }
  },
  mounted() {
    this.initMap()
  },
  methods: {
    async initMap() {
      try {
        // Load Google Maps script dynamically
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAr_XDKiLIEVSHXUVn7ZDBMIapw7BbdZsc&libraries=places,geometry&callback=initGoogleMap`
        script.async = true
        script.defer = true

        // Create a promise that resolves when the script loads
        window.initGoogleMap = () => {
          this.initializeMap()
        }

        document.head.appendChild(script)
      } catch (error) {
        console.error('❌ Google Maps 加载错误:', error)
        this.loading = false
        alert(`⚠️ Google Maps 加载失败\n\n错误信息: ${error.message}\n\n请检查网络连接和 API Key 配置。`)
      }
    },
    initializeMap() {
      try {
        // Initialize map
        this.map = new google.maps.Map(this.$refs.mapContainer, {
          center: this.center,
          zoom: 12,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true
        })

        // Initialize geocoder and info window
        this.geocoder = new google.maps.Geocoder()
        this.infoWindow = new google.maps.InfoWindow()

        // Add event markers
        this.addEventMarkers()

        this.loading = false
      } catch (error) {
        console.error('❌ 地图初始化错误:', error)
        this.loading = false
        alert(`⚠️ 地图初始化失败: ${error.message}`)
      }
    },
    addEventMarkers() {
      // Clear existing markers
      this.clearMarkers()

      this.events.forEach((event, index) => {
        // Geocode the event location
        this.geocodeAddress(event.location, (location) => {
          if (location) {
            const marker = new google.maps.Marker({
              position: location,
              map: this.map,
              title: event.title,
              label: {
                text: String(index + 1),
                color: 'white'
              },
              animation: google.maps.Animation.DROP
            })

            // Add click listener to show info window
            marker.addListener('click', () => {
              this.showEventInfo(event, marker)
            })

            this.markers.push(marker)
          }
        })
      })
    },
    geocodeAddress(address, callback) {
      if (!this.geocoder) return

      this.geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          callback(results[0].geometry.location)
        } else {
          console.error('Geocoding failed for address:', address, status)
          callback(null)
        }
      })
    },
    showEventInfo(event, marker) {
      const content = `
        <div class="event-info-window">
          <h6>${event.title}</h6>
          <p><i class="bi bi-geo-alt"></i> ${event.location}</p>
          <p><i class="bi bi-calendar"></i> ${this.formatDate(event.time_start)}</p>
          <p><i class="bi bi-people"></i> ${event.participants}/${event.capacity} participants</p>
          <button class="btn btn-sm btn-primary" onclick="window.location.href='/events/${event.id}'">
            View Details
          </button>
          ${this.userLocation ? `
            <button class="btn btn-sm btn-success ms-2" data-event-id="${event.id}">
              Get Directions
            </button>
          ` : ''}
        </div>
      `

      this.infoWindow.setContent(content)
      this.infoWindow.open(this.map, marker)

      // Add listener for directions button
      if (this.userLocation) {
        google.maps.event.addListenerOnce(this.infoWindow, 'domready', () => {
          const dirButton = document.querySelector(`[data-event-id="${event.id}"]`)
          if (dirButton) {
            dirButton.addEventListener('click', () => {
              this.selectedEvent = event
              this.calculateRoute(marker.getPosition())
            })
          }
        })
      }
    },
    clearMarkers() {
      this.markers.forEach(marker => marker.setMap(null))
      this.markers = []
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      } catch {
        return 'N/A'
      }
    }
  },
  watch: {
    events() {
      if (this.map) {
        this.addEventMarkers()
      }
    }
  }
}
</script>

<style scoped>
.event-map-container {
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 12px;
  overflow: hidden;
}

.map-canvas {
  width: 100%;
  height: 100%;
}

.map-info-panel {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: white;
  padding: 12px 24px;
  border-radius: 50px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.info-content {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.info-content i {
  color: #9C27B0;
  font-size: 16px;
}

.map-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  background: white;
  padding: 30px 40px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 20;
}

@media (max-width: 768px) {
  .event-map-container {
    height: 450px;
  }

  .map-info-panel {
    top: 10px;
    padding: 10px 18px;
  }

  .info-content {
    font-size: 12px;
  }
}
</style>
